import { Skeleton, Tab, Tabs } from "@material-ui/core";
import Label from "components/Label";
import { useTranslation } from "react-i18next";
import { getFilteredEventsByType } from "utils/calendar/filters";
import type { MouseEvent } from "react";
import type { LabelColor } from "components/Label";
import type { CalendarEvent } from "../../../@types/sharedSchema";

type CalendarTasksTabsProps = {
  events: CalendarEvent[];
  loading: boolean;
  /**
   * Open tasks the user put in colleagues' calendars — shows the "assigned by me" tab. Undefined
   * hides it (a user with no office has nobody to assign to); null while it loads.
   */
  assignedOpenCount?: number | null;
  currentTab: null | EventTaskTab;
  onCurrentTabChange: (event: MouseEvent, newValue: EventTaskTab) => void;
};

export function CalendarTasksTabs({
  events,
  loading,
  assignedOpenCount,
  currentTab,
  onCurrentTabChange
}: CalendarTasksTabsProps): JSX.Element {
  const { t: translate } = useTranslation();

  type CalendarTasksTabsStatus = {
    id: EventTaskTab;
    color: LabelColor;
    count?: number;
    loading?: boolean;
  };

  const calendarTasksTabsStatus: CalendarTasksTabsStatus[] = [
    {
      id: "overdue",
      color: "default",
      count: getFilteredEventsByType(events, "overdue").length
    },
    {
      id: "today",
      color: "default",
      count: getFilteredEventsByType(events, "today").length
    },
    {
      id: "thisWeek",
      color: "default",
      count: getFilteredEventsByType(events, "thisWeek").length
    },
    {
      id: "upcoming",
      color: "default",
      count: getFilteredEventsByType(events, "upcoming").length
    },
    {
      id: "done",
      color: "default",
      count: getFilteredEventsByType(events, "done").length
    },
    ...(assignedOpenCount === undefined
      ? []
      : [
          {
            id: "assignedByMe" as const,
            color: "default" as const,
            count: assignedOpenCount ?? 0,
            loading: assignedOpenCount === null
          }
        ])
  ];

  return (
    <Tabs sx={{ mx: 2 }} variant="scrollable" value={currentTab} onChange={onCurrentTabChange}>
      {calendarTasksTabsStatus.map(({ id, color, count, loading: tabLoading }) => {
        const showCountOnLabel = count !== undefined;

        return (
          <Tab
            value={id}
            label={translate(`calendarPage.tasks.tabs.${id}`)}
            icon={
              showCountOnLabel ? (
                loading || tabLoading ? (
                  <Skeleton sx={{ borderRadius: 1 }} variant="circular" width={22} height={22} />
                ) : (
                  <Label color={color}>{count}</Label>
                )
              ) : undefined
            }
            key={id}
          />
        );
      })}
    </Tabs>
  );
}

// `assignedByMe`: office tasks the user put in colleagues' calendars (CalendarContext.assignedTasks).
const eventTasksTab = ["done", "today", "overdue", "thisWeek", "upcoming", "assignedByMe"] as const;

export type EventTaskTab = (typeof eventTasksTab)[number];
